import { Injectable } from '@angular/core';
import { MAX_PATIENT_AGE } from 'src/shared/constants/max-patient-age.const';
import { CustomValidationErrors } from 'src/app/models/custom-validation-errors.model';
import { ValidationErrorNamespaceEnum } from 'src/shared/enums/validation-error-namespace.enum';
import { PHONE_NUMBER_MAX_LENGTH } from 'src/shared/components/phone/phone.util';

@Injectable()
export class CustomErrorMessagesService {
  public getErrorMessage(errors: CustomValidationErrors | undefined | null): string {
    if (!errors) {
      return '';
    }

    if (errors.unsupportedSavedValue) {
      return errors.unsupportedSavedValue.message;
    }

    if (errors.required || errors.requiredNotNull) {
      return 'This field is required.';
    }

    if (errors.invalidSpacing) {
      return `Only a single space character is allowed between strings. Leading spaces or multiple space characters between strings are not allowed.`;
    }

    if (errors.invalidName) {
      return `${errors.invalidName.fieldName} must be ${errors.invalidName.maxLength} characters or less and can only include the following characters: a-z, A-Z, ${errors.invalidName.allowNumbers ? '0-9, ' : ''}. (period), ' (single quote), - (dash/hyphen), (spaces)`;
    }
    if (errors.invalidBirthDate) {
      return 'Enter a valid date of birth.';
    }
    if (errors.futureBirthDate) {
      return 'Enter a valid date of birth. The date of birth provided cannot be a date in the future.';
    }
    if (errors.tooOldBirthDate) {
      return `Enter a valid date of birth. The date of birth provided cannot be more than ${MAX_PATIENT_AGE} years ago.`;
    }
    if (errors.invalidZipCode) {
      if (errors.invalidZipCode === 'US') {
        return 'US zip codes must be in the following format(s): ##### or #####-####';
      } else if (errors.invalidZipCode === 'CA') {
        return 'Canadian zip codes must be in the following format: A#A #A#';
      }
    }
    if (errors.invalidBillingZipCode) {
      return `Billing Zip Code must be ${errors.invalidBillingZipCode.maxLength} characters or less and can only include the following characters: a-z, A-Z, 0-9, - (dash/hyphen)`;
    }
    if (errors.invalidPhoneNumber) {
      if (errors.invalidPhoneNumber.onlyNumeric) {
        return 'Only numerical values can be provided for phone numbers.';
      }
      if (errors.invalidPhoneNumber.exceedMaxLength) {
        return `Phone number must be ${errors.invalidPhoneNumber.maxLength ?? PHONE_NUMBER_MAX_LENGTH} characters or less.`;
      }
      if (errors.invalidPhoneNumber.invalidLength) {
        return `Phone number "${errors.invalidPhoneNumber.actualValue ?? ''}" shouldn't be longer or shorter than 10.`;
      }
    }
    if (errors.phoneCountryCodeRequired) {
      return 'Country code and phone number must be provided together.';
    }
    if (errors.usOnlyCountryCode) {
      return `Only US fax numbers (+1) are supported. This number has country code +${errors.usOnlyCountryCode.countryCode}.`;
    }
    if (errors.customMaxLength) {
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.City) {
        return `City addresses must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.State) {
        return `State addresses must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.Email) {
        return `Email must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.Street) {
        return `Street addresses must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
      if (errors.customMaxLength.validationErrorNamespace === ValidationErrorNamespaceEnum.PatientUserDefinedId) {
        return `Patient ID must be ${errors.customMaxLength.requiredLength} characters or less.`;
      }
    }

    if (errors.accessionNumberInvalid) {
      if (errors.accessionNumberInvalid.reason === 'notNumeric') {
        return 'Accession number must contain only numeric characters.';
      }
      if (errors.accessionNumberInvalid.reason === 'invalidLength') {
        return `Accession number must be exactly ${9} digits long.`;
      }
    }

    if (errors.leadingZero) {
      return errors.leadingZero.message ?? 'Value cannot start with 0.';
    }

    if (errors.minlength) {
      return `Value must be at least ${errors.minlength.requiredLength} characters long.`;
    }

    if (errors.timeRange) {
      if (errors.timeRange.fieldName === ValidationErrorNamespaceEnum.PatientArrivalTime) {
        if (errors.timeRange.minTime) {
          return `The Patient Arrival Time must be within ${errors.timeRange.minTime} minutes of the current time.`;
        }
        if (errors.timeRange.maxTime !== undefined) {
          return 'Patient Arrival Time cannot be a future time.';
        }
        return 'Invalid Patient Arrival Time.';
      }
    }

    if (errors.invalidNumberRange) {
      return 'From value must be less than to value';
    }

    if (errors.invalidNpi) {
      if (errors.invalidNpi.countryCode === 'US') {
        return `NPI must be a ${errors.invalidNpi.maxLength} digit number.`;
      } else if (errors.invalidNpi.alphanumericOnly) {
        return `NPI must be ${errors.invalidNpi.maxLength} characters or less and can only include letters and digits.`;
      } else {
        return `NPI must be ${errors.invalidNpi.maxLength} characters or less.`;
      }
    }

    if (errors.sameAsOrderingProvider) {
      return 'The same provider cannot be selected as an Ordering Provider and a Copy To Provider. Please select a different provider.';
    }

    if (errors.specimenQuantityRange) {
      return 'Specimen quantity must be between 1 and 30.';
    }

    return 'Invalid field value.';
  }
}
